import React, { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from "react";
import { FcCollapse } from "react-icons/fc";

interface DialogContextType {
  openDialog: (content: ReactNode, header?: ReactNode) => void;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode>(null);
  const [header, setHeader] = useState<ReactNode>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);

  const openDialog = useCallback((dialogContent: ReactNode, dialogHeader?: ReactNode) => {
    previouslyFocused.current = document.activeElement as HTMLElement;
    setContent(dialogContent);
    setHeader(dialogHeader || null);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setContent(null);
      setHeader(null);
      previouslyFocused.current?.focus();
    }, 200);
  }, []);

  // Trap focus inside modal
  useEffect(() => {
    if (!isOpen) return;

    const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );

    const firstEl = focusableElements?.[0];
    const lastEl = focusableElements?.[focusableElements.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        if (!focusableElements || focusableElements.length === 0) return;

        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl?.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl?.focus();
          }
        }
      }
      if (e.key === "Escape") {
        closeDialog();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    firstEl?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeDialog]);

  // Hide background from screen readers when modal is open
  useEffect(() => {
    if (isOpen && backgroundRef.current) {
      backgroundRef.current.setAttribute("aria-hidden", "true");
      backgroundRef.current.inert = true; // Prevent focus/tabbing
    } else if (backgroundRef.current) {
      backgroundRef.current.removeAttribute("aria-hidden");
      backgroundRef.current.inert = false;
    }
  }, [isOpen]);

  return (
    <DialogContext.Provider value={{ openDialog, closeDialog }}>
      <div ref={backgroundRef}>{children}</div>

      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-header"
          aria-describedby="dialog-content"
        >
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm dark:bg-white/50"
            onClick={closeDialog}
          />

          {/* Dialog */}
          <div className="relative z-10 w-full max-w-2xl px-2 flex justify-center">
            <div
              ref={dialogRef}
              className="pointer-events-auto bg-white dark:bg-gray-800 dark:text-gray-100 rounded-2xl shadow-xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scaleIn"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              {header ? (
                <div
                  id="dialog-header"
                  className="h-[50px] sticky top-0 z-20 px-4 py-0 flex justify-between items-center bg-white dark:bg-gray-800"
                >
                  <div className="w-full text-green-900 dark:text-gray-100 mt-10 mb-7">{header}</div>
                  <button
                    onClick={closeDialog}
                    className="text-gray-500 dark:text-gray-100 hover:text-gray-700"
                    aria-label="Close dialog"
                  >
                    <FcCollapse size={24} />
                  </button>
                </div>
              ):
              <div className="absolute top-0 z-20 px-2 py-2">
                  <button
                      onClick={closeDialog}
                      className="text-gray-500 dark:text-gray-100 hover:text-gray-700"
                      aria-label="Close dialog"
                    >
                      <FcCollapse size={24} />
                  </button>
              </div>
             
              }

              {/* Content */}
              <div id="dialog-content" className="overflow-y-auto max-h-[80vh] p-0 sm:p-0 md:p-2 lg:p-4 flex-1">
                {content}
              </div>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext);
  if (!context) throw new Error("useDialog must be used within a DialogProvider");
  return context;
};